import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tree,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  LinkOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import RichTextEditor from "components/common/RichTextEditor";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import * as pmService from "services/pmService";
import apiClient from "utils/apiClient";
import {
  convertArrayRefsToFileService,
  processDescriptionImages,
} from "utils/taskUtils";
import { STORAGE_BASE_URL } from "constants";
import { OLIVEEB_COLORS } from "workspaces/colors";
import paths from "routes/paths";

const { Paragraph, Text, Title } = Typography;

const emptyDoc = {
  title: "",
  content: "",
  imageFiles: [],
  parentId: null,
  position: null,
  isNewDraft: false,
};

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
]);
const SUPPORTED_IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg)$/i;
const FILE_SERVICE_REGEX = /file-service:\/\/([0-9a-fA-F-]{36})/i;

const createImageMaps = () => ({
  byUrl: new Map(),
  byUrlBase: new Map(),
  byFileId: new Map(),
});

const normalizeUrl = (url = "") => String(url || "").split("?")[0];

const resolveResponseUrl = (payload, visited = new WeakSet()) => {
  if (!payload) return null;
  if (typeof payload === "string") return payload;
  if (typeof payload !== "object") return null;
  if (visited.has(payload)) return null;
  visited.add(payload);

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const resolved = resolveResponseUrl(item, visited);
      if (resolved) return resolved;
    }
    return null;
  }

  for (const field of ["uri", "url", "downloadUrl", "download_url", "signedUrl"]) {
    if (typeof payload[field] === "string" && payload[field].trim()) {
      return payload[field];
    }
  }

  for (const value of Object.values(payload)) {
    const resolved = resolveResponseUrl(value, visited);
    if (resolved) return resolved;
  }

  return null;
};

const parseHtml = (html = "") => {
  const parser = new DOMParser();
  return parser.parseFromString(html || "", "text/html");
};

const pixelValue = (value) => {
  const match = String(value || "").match(/(\d+(?:\.\d+)?)/);
  return match ? Math.max(1, Math.round(Number(match[1]))) : null;
};

const stylePixelValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw || raw.endsWith("%")) return null;
  return pixelValue(raw);
};

const imageDimension = (img, name, fallback) => {
  const styleValue = stylePixelValue(img.style?.[name]);
  if (styleValue) return styleValue;
  const attrValue = pixelValue(img.getAttribute(name));
  if (attrValue) return attrValue;
  return Math.max(1, Number(fallback) || 1);
};

const imageInfoFromMetadata = (imageFiles = []) => {
  const byFileId = new Map();
  imageFiles.forEach((image) => {
    if (!image?.fileId) return;
    byFileId.set(String(image.fileId), {
      fileId: String(image.fileId),
      width: image.width || 1,
      height: image.height || 1,
      fileSize: image.fileSize || 0,
    });
  });
  return byFileId;
};

const extractStoredImageRefs = (html = "", imageFiles = []) => {
  const metadata = imageInfoFromMetadata(imageFiles);
  const doc = parseHtml(convertArrayRefsToFileService(html || ""));
  return Array.from(doc.body.querySelectorAll("img"))
    .map((img) => {
      const src = img.getAttribute("src") || "";
      const fileId = src.match(FILE_SERVICE_REGEX)?.[1];
      if (!fileId) return null;
      const info = metadata.get(fileId) || {};
      return {
        fileId,
        width: imageDimension(img, "width", info.width),
        height: imageDimension(img, "height", info.height),
        fileSize: info.fileSize || 0,
      };
    })
    .filter(Boolean);
};

const seedImageMaps = (rawHtml, processedHtml, imageFiles) => {
  const maps = createImageMaps();
  const storedRefs = extractStoredImageRefs(rawHtml, imageFiles);
  const processedDoc = parseHtml(processedHtml || "");
  const processedImages = Array.from(processedDoc.body.querySelectorAll("img"));

  storedRefs.forEach((info, index) => {
    maps.byFileId.set(info.fileId, info);
    const src = processedImages[index]?.getAttribute("src");
    if (src) {
      maps.byUrl.set(src, info);
      maps.byUrlBase.set(normalizeUrl(src), info);
    }
    maps.byUrl.set(`file-service://${info.fileId}`, info);
  });

  return maps;
};

const isSupportedImage = (file) => {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return SUPPORTED_IMAGE_TYPES.has(type) || SUPPORTED_IMAGE_EXTENSIONS.test(name);
};

const readableSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const imageExtension = (file) => {
  const type = String(file?.type || "").toLowerCase();
  if (type === "image/jpeg" || type === "image/jpg") return "jpg";
  if (type === "image/svg+xml") return "svg";
  const subtype = type.split("/")[1];
  if (subtype) return subtype;
  const match = String(file?.name || "").match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : "png";
};

const ensureNamedImageFile = (file) => {
  if (file?.name) return file;
  return new File([file], `pasted-${Date.now()}.${imageExtension(file)}`, {
    type: file?.type || "image/png",
  });
};

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: img.naturalWidth || img.width || 1,
        height: img.naturalHeight || img.height || 1,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected image could not be read."));
    };
    img.src = objectUrl;
  });

const flattenDocs = (items = []) => {
  const list = [];
  const visit = (nodes = []) => {
    nodes.forEach((node) => {
      list.push(node);
      if (Array.isArray(node.children) && node.children.length) {
        visit(node.children);
      }
    });
  };
  visit(items);
  return list;
};

const sortDocs = (items = []) =>
  [...items].sort((a, b) => {
    const positionA = Number.isFinite(Number(a?.position)) ? Number(a.position) : Number.MAX_SAFE_INTEGER;
    const positionB = Number.isFinite(Number(b?.position)) ? Number(b.position) : Number.MAX_SAFE_INTEGER;
    if (positionA !== positionB) return positionA - positionB;
    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });

const findDocPath = (items = [], targetId) => {
  if (!targetId) return [];
  const visit = (nodes = [], trail = []) => {
    for (const node of sortDocs(nodes)) {
      const nextTrail = [...trail, node];
      if (node.id === targetId) return nextTrail;
      const found = visit(node.children || [], nextTrail);
      if (found.length) return found;
    }
    return [];
  };
  return visit(items);
};

const toTreeData = (items = [], selectedId, handlers) =>
  sortDocs(items).map((doc) => {
    const children = Array.isArray(doc.children) ? doc.children : [];
    const menuItems = [
      { key: "child", label: "New subdocument", icon: <PlusOutlined /> },
      { key: "sibling", label: "New document beside", icon: <PlusOutlined /> },
      { type: "divider" },
      { key: "rename", label: "Rename", icon: <EditOutlined /> },
      {
        key: "delete",
        label: "Delete document",
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];
    return {
      key: doc.id,
      title: (
        <div className={`docs-tree-title${selectedId === doc.id ? " is-selected" : ""}`}>
          <span className="docs-tree-name">
            {children.length ? <FolderOpenOutlined /> : <FileTextOutlined />}
            <span
              className="docs-tree-label"
              title="Double-click to rename"
              onDoubleClick={(event) => {
                event.stopPropagation();
                handlers.editTitle(doc);
              }}
            >
              {doc.title || "Untitled doc"}
            </span>
          </span>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: menuItems,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === "child") handlers.addChild(doc);
                if (key === "sibling") handlers.addSibling(doc);
                if (key === "rename") handlers.editTitle(doc);
                if (key === "delete") handlers.confirmDelete(doc);
              },
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Actions for ${doc.title || "doc"}`}
            />
          </Dropdown>
        </div>
      ),
      children: toTreeData(children, selectedId, handlers),
    };
  });

const Docs = () => {
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const { docId: routeDocId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [docs, setDocs] = useState([]);
  const [selectedId, setSelectedId] = useState(routeDocId);
  const [draft, setDraft] = useState(emptyDoc);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProperty, setSavingProperty] = useState("");
  const [editingProperty, setEditingProperty] = useState("");
  const [isTreeCollapsed, setIsTreeCollapsed] = useState(false);
  const [docAttachments, setDocAttachments] = useState([]);
  const [loadingDocAttachments, setLoadingDocAttachments] = useState(false);
  const [uploadingDocAttachment, setUploadingDocAttachment] = useState(false);
  const [deletingDocAttachmentId, setDeletingDocAttachmentId] = useState(null);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [pendingTitleEditId, setPendingTitleEditId] = useState("");
  const imageMapsRef = useRef(createImageMaps());
  const uploadCacheRef = useRef(new Map());
  const latestContentRef = useRef("");

  useEffect(() => {
    latestContentRef.current = draft.content || "";
  }, [draft.content]);

  const updateDraftContent = useCallback((content) => {
    latestContentRef.current = content || "";
    setDraft((current) => ({ ...current, content: content || "" }));
  }, []);

  const flatDocs = useMemo(() => flattenDocs(docs), [docs]);
  const selectedDoc = useMemo(
    () => flatDocs.find((doc) => doc.id === selectedId) || null,
    [flatDocs, selectedId],
  );
  const hasDocs = flatDocs.length > 0;
  const hasActiveDraft = Boolean(
    draft.id ||
      draft.isNewDraft ||
      draft.title ||
      draft.content ||
      draft.slug ||
      draft.parentId,
  );
  const isNew = !draft.id && Boolean(draft.isNewDraft);
  const docPath = useMemo(() => {
    if (draft.id) {
      const path = findDocPath(docs, draft.id);
      if (path.length) {
        return path.map((doc, index) =>
          index === path.length - 1
            ? { ...doc, title: draft.title || doc.title || "Untitled doc" }
            : doc,
        );
      }
      return [{ id: draft.id, title: draft.title || "Untitled doc" }];
    }

    if (draft.parentId) {
      return [
        ...findDocPath(docs, draft.parentId),
        { id: "", title: draft.title || "Untitled doc" },
      ];
    }

    return hasActiveDraft ? [{ id: "", title: draft.title || "Untitled doc" }] : [];
  }, [docs, draft.id, draft.parentId, draft.title, hasActiveDraft]);

  const navigateToDocs = useCallback(
    (docId = "", options = {}) => {
      navigate(
        {
          pathname: docId ? paths.docDetails(docId) : paths.docs,
          search: location.search,
        },
        options,
      );
    },
    [location.search, navigate],
  );

  const fetchSignedImageUrl = useCallback(async (fileId) => {
    if (!fileId) return null;
    try {
      const response = await apiClient.get(
        `${STORAGE_BASE_URL}/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(fileId)}`,
      );
      return resolveResponseUrl(response?.data);
    } catch (error) {
      console.error("[Docs] failed to fetch signed image URL", {
        fileId,
        message: error?.message,
      });
      return null;
    }
  }, []);

  const rememberImage = useCallback((src, info) => {
    if (!info?.fileId) return;
    const normalizedInfo = {
      fileId: String(info.fileId),
      width: info.width || 1,
      height: info.height || 1,
      fileSize: info.fileSize || 0,
    };
    imageMapsRef.current.byFileId.set(normalizedInfo.fileId, normalizedInfo);
    if (src) {
      imageMapsRef.current.byUrl.set(src, normalizedInfo);
      imageMapsRef.current.byUrlBase.set(normalizeUrl(src), normalizedInfo);
    }
    imageMapsRef.current.byUrl.set(`file-service://${normalizedInfo.fileId}`, normalizedInfo);
  }, []);

  const resolveImageInfo = useCallback((src) => {
    const directFileId = String(src || "").match(FILE_SERVICE_REGEX)?.[1];
    if (directFileId) {
      return imageMapsRef.current.byFileId.get(directFileId) || {
        fileId: directFileId,
        width: 1,
        height: 1,
        fileSize: 0,
      };
    }
    return (
      imageMapsRef.current.byUrl.get(src) ||
      imageMapsRef.current.byUrlBase.get(normalizeUrl(src))
    );
  }, []);

  const normalizeDocContentForSave = useCallback(
    (html = "") => {
      const doc = parseHtml(html || "");
      const imageFilesById = new Map();

      Array.from(doc.body.querySelectorAll("img")).forEach((img) => {
        const src = img.getAttribute("src") || "";
        const info = resolveImageInfo(src);
        if (!info?.fileId) return;

        const width = imageDimension(img, "width", info.width);
        const height = imageDimension(img, "height", info.height);
        const fileSize = Math.max(0, Number(info.fileSize) || 0);
        const normalizedInfo = {
          fileId: info.fileId,
          width,
          height,
          fileSize,
        };

        img.setAttribute("src", `file-service://${info.fileId}`);
        img.setAttribute("width", String(width));
        img.setAttribute("height", String(height));
        img.setAttribute("alt", img.getAttribute("alt") || "image");
        img.setAttribute("loading", "lazy");
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        img.style.width = `${width}px`;
        img.style.height = `${height}px`;
        img.style.maxWidth = "100%";
        img.style.display = "inline-block";
        img.style.verticalAlign = "middle";
        img.style.margin = img.style.margin || "6px 4px";
        img.style.opacity = "";
        img.style.cursor = "";

        rememberImage(src, normalizedInfo);
        imageFilesById.set(info.fileId, normalizedInfo);
      });

      return {
        content: doc.body.innerHTML,
        imageFiles: Array.from(imageFilesById.values()),
      };
    },
    [rememberImage, resolveImageInfo],
  );

  const uploadImageOnce = useCallback(
    async (file, dimensions) => {
      const signature = [
        file.name,
        file.size,
        file.type,
        file.lastModified || 0,
      ].join(":");

      if (!uploadCacheRef.current.has(signature)) {
        uploadCacheRef.current.set(
          signature,
          uploadFile(user?.id, file, {
            purpose: "TASK_PHOTOS",
            fileType: "PHOTO",
          }).then((result) => ({
            ...result,
            width: dimensions.width || 1,
            height: dimensions.height || 1,
            fileSize: result?.size ?? file.size,
          })),
        );
      }

      return uploadCacheRef.current.get(signature);
    },
    [uploadFile, user?.id],
  );

  const handleDocImageUpload = useCallback(
    async (rawFile, helpers) => {
      const {
        insertPlaceholder,
        replacePlaceholderWithText,
        replacePlaceholderWithImage,
        getHtml,
      } = helpers;
      const file = ensureNamedImageFile(rawFile);
      const placeholderRange = insertPlaceholder(`Uploading ${file.name}...`);

      try {
        if (!isSupportedImage(file)) {
          throw new Error("Unsupported image type. Use PNG, JPG, JPEG, GIF, WebP, or SVG.");
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          throw new Error(`Image is too large. Maximum size is ${readableSize(MAX_IMAGE_SIZE_BYTES)}.`);
        }

        const dimensions = await getImageDimensions(file);
        const uploadResult = await uploadImageOnce(file, dimensions);
        const fileId = uploadResult?.fileId;
        if (!fileId) {
          throw new Error("Upload finished without a file id.");
        }

        const signedUrl =
          (await fetchSignedImageUrl(fileId)) ||
          uploadResult?.meta?.url ||
          uploadResult?.url ||
          `file-service://${fileId}`;
        const info = {
          fileId,
          width: dimensions.width || 1,
          height: dimensions.height || 1,
          fileSize: uploadResult?.fileSize ?? uploadResult?.size ?? file.size,
        };

        rememberImage(signedUrl, info);
        replacePlaceholderWithImage(placeholderRange, signedUrl, {
          width: dimensions.width,
          height: dimensions.height,
          alt: file.name.replace(/\.[^/.]+$/, "") || "image",
        });
        const html = getHtml();
        updateDraftContent(html);
      } catch (error) {
        console.error("[Docs] image upload failed", error);
        replacePlaceholderWithText(placeholderRange, "(image upload failed)");
        updateDraftContent(getHtml());
        message.error(error?.message || "Failed to upload image.");
      }
    },
    [fetchSignedImageUrl, rememberImage, updateDraftContent, uploadImageOnce],
  );

  const loadDocAttachments = useCallback(async (docId) => {
    if (!docId) {
      setDocAttachments([]);
      return;
    }
    setLoadingDocAttachments(true);
    try {
      const attachments = await pmService.listOperationAttachments("docs", docId);
      setDocAttachments(Array.isArray(attachments) ? attachments : []);
    } catch (error) {
      console.error("[Docs] failed to load attachments", error);
      message.error(error?.message || "Failed to load document attachments.");
    } finally {
      setLoadingDocAttachments(false);
    }
  }, []);

  useEffect(() => {
    setDocAttachments([]);

    if (!attachmentsOpen || !draft.id) {
      setLoadingDocAttachments(false);
      return;
    }

    loadDocAttachments(draft.id);
  }, [attachmentsOpen, draft.id, loadDocAttachments]);

  const handleDocAttachmentUpload = async (file) => {
    if (!draft.id) {
      message.error("Save the document before attaching files.");
      return;
    }
    if (!actorId) {
      message.error("User ID is required to attach files.");
      return;
    }

    setUploadingDocAttachment(true);
    try {
      const uploaded = await uploadFile(actorId, file, {
        purpose: "PM_ATTACHMENT",
        fileType: file.type,
      });
      const created = await pmService.addOperationAttachments(
        "docs",
        draft.id,
        [{
          fileId: uploaded.fileId,
          fileName: file.name,
          label: file.name,
        }],
        actorId,
      );
      setDocAttachments((current) => [...created, ...current]);
      message.success("File attached.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to attach file.");
      throw error;
    } finally {
      setUploadingDocAttachment(false);
    }
  };

  const handleDeleteDocAttachment = async (attachmentId) => {
    if (!draft.id || !attachmentId) return;

    setDeletingDocAttachmentId(attachmentId);
    try {
      await pmService.deleteOperationAttachment("docs", draft.id, attachmentId, actorId);
      setDocAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
      message.success("Attachment deleted.");
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to delete attachment.");
    } finally {
      setDeletingDocAttachmentId(null);
    }
  };

  const loadTree = useCallback(async ({ keepSelection = true } = {}) => {
    setLoadingTree(true);
    try {
      const tree = await pmService.listDocTree();
      const nextDocs = Array.isArray(tree) ? tree : [];
      setDocs(nextDocs);
      const ids = flattenDocs(nextDocs).map((doc) => doc.id).filter(Boolean);
      setExpandedKeys((current) => {
        const validExpandedKeys = keepSelection
          ? current.filter((key) => ids.includes(key))
          : [];
        return validExpandedKeys;
      });
      if (!keepSelection) {
        if (routeDocId) {
          setSelectedId(routeDocId);
          return;
        }
        setSelectedId("");
        setDraft(emptyDoc);
        return;
      }
      if (selectedId && !ids.includes(selectedId)) {
        setSelectedId("");
        setDraft(emptyDoc);
        navigateToDocs("", { replace: true });
      }
    } catch (error) {
      message.error(error?.message || "Failed to load docs.");
    } finally {
      setLoadingTree(false);
    }
  }, [navigateToDocs, routeDocId, selectedId]);

  const loadDoc = useCallback(async (docId) => {
    if (!docId) return;
    setLoadingDoc(true);
    try {
      const doc = await pmService.getDoc(docId);
      const rawContent = convertArrayRefsToFileService(doc.content || "");
      const processedContent = await processDescriptionImages(
        rawContent,
        doc.imageFiles || [],
      );
      imageMapsRef.current = seedImageMaps(
        rawContent,
        processedContent || rawContent,
        doc.imageFiles || [],
      );
      setDraft({
        id: doc.id,
        parentId: doc.parentId || null,
        title: doc.title || "",
        slug: doc.slug || "",
        content: processedContent || rawContent || "",
        imageFiles: doc.imageFiles || [],
        position: doc.position ?? null,
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
      });
      setEditingProperty("");
    } catch (error) {
      message.error(error?.message || "Failed to open doc.");
      setSelectedId("");
      setDraft(emptyDoc);
      navigateToDocs("", { replace: true });
    } finally {
      setLoadingDoc(false);
    }
  }, [navigateToDocs]);

  useEffect(() => {
    loadTree({ keepSelection: false });
  }, []);

  useEffect(() => {
    if (routeDocId && routeDocId !== selectedId && !draft.isNewDraft) {
      setSelectedId(routeDocId);
      setDraft(emptyDoc);
      setEditingProperty("");
    }
  }, [draft.isNewDraft, routeDocId, selectedId]);

  useEffect(() => {
    if (selectedId) {
      loadDoc(selectedId);
    }
  }, [loadDoc, selectedId]);

  const startDraft = (parentId = null) => {
    setSelectedId("");
    setEditingProperty("");
    imageMapsRef.current = createImageMaps();
    uploadCacheRef.current.clear();
    navigateToDocs("");
    setDraft({
      ...emptyDoc,
      parentId,
      isNewDraft: true,
      title: "",
      content: "",
    });
  };

  const addRoot = () => startDraft(null);
  const addChild = (doc = selectedDoc) => startDraft(doc?.id || null);
  const addSibling = (doc = selectedDoc) => startDraft(doc?.parentId || null);

  const selectDoc = (docId = "") => {
    if (!docId) return;
    const path = findDocPath(docs, docId);
    const hierarchyKeys = path.slice(0, -1).map((item) => item.id).filter(Boolean);
    const target = path[path.length - 1];
    if (target?.children?.length) {
      hierarchyKeys.push(docId);
    }
    if (hierarchyKeys.length) {
      setExpandedKeys((current) => [...new Set([...current, ...hierarchyKeys])]);
    }
    setSelectedId(docId);
    setEditingProperty("");
    navigateToDocs(docId);
  };

  const editDocTitle = (doc) => {
    if (!doc?.id) return;
    if (draft.id === doc.id) {
      setEditingProperty("title");
      return;
    }
    setPendingTitleEditId(doc.id);
    selectDoc(doc.id);
  };

  useEffect(() => {
    if (pendingTitleEditId && draft.id === pendingTitleEditId && !loadingDoc) {
      setEditingProperty("title");
      setPendingTitleEditId("");
    }
  }, [draft.id, loadingDoc, pendingTitleEditId]);

  const copyDocLink = async () => {
    if (!draft.id) return;
    const link = `${window.location.origin}${paths.docDetails(draft.id)}${location.search || ""}`;
    try {
      await navigator.clipboard.writeText(link);
      message.success("Document link copied.");
    } catch {
      message.info("Use the browser address bar to copy this document link.");
    }
  };

  const saveDoc = async () => {
    const title = draft.title.trim();
    if (!title) {
      message.warning("Enter a doc title before saving.");
      return;
    }

    setSaving(true);
    try {
      const normalized = normalizeDocContentForSave(
        latestContentRef.current || draft.content || "",
      );
      const payload = {
        parentId: draft.parentId || null,
        title,
        slug: draft.slug?.trim() || undefined,
        content: normalized.content,
        position: draft.position ?? undefined,
        imageFiles: normalized.imageFiles,
      };
      const saved = draft.id
        ? await pmService.updateDoc(draft.id, payload, actorId)
        : await pmService.createDoc(payload, actorId);
      message.success(draft.id ? "Doc updated." : "Doc created.");
      setSelectedId(saved.id);
      navigateToDocs(saved.id, { replace: !draft.id });
      await loadTree({ keepSelection: true });
      await loadDoc(saved.id);
    } catch (error) {
      message.error(error?.message || "Unable to save doc.");
    } finally {
      setSaving(false);
    }
  };

  const saveDocProperty = async (name) => {
    if (!draft.id || !name) return;
    if (name === "content") {
      setSavingProperty(name);
      try {
        const normalized = normalizeDocContentForSave(latestContentRef.current || draft.content || "");
        const saved = await pmService.updateDoc(
          draft.id,
          {
            parentId: draft.parentId || null,
            title: draft.title.trim(),
            slug: draft.slug?.trim() || undefined,
            content: normalized.content,
            position: draft.position ?? undefined,
            imageFiles: normalized.imageFiles,
          },
          actorId,
        );
        setEditingProperty("");
        message.success("Document updated.");
        await loadTree({ keepSelection: true });
        await loadDoc(saved.id);
      } catch (error) {
        message.error(error?.message || "Unable to update document.");
      } finally {
        setSavingProperty("");
      }
      return;
    }

    const value = name === "title" ? draft.title.trim() : draft[name] || "";
    if (name === "title" && !value) {
      message.warning("Enter a doc title before saving.");
      return;
    }

    setSavingProperty(name);
    try {
      const normalizedDocument =
        name === "title"
          ? normalizeDocContentForSave(
              latestContentRef.current || draft.content || "",
            )
          : null;
      const saved =
        name === "title"
          ? await pmService.updateDoc(
              draft.id,
              {
                parentId: draft.parentId || null,
                title: value,
                slug: draft.slug?.trim() || undefined,
                content: normalizedDocument.content,
                position: draft.position ?? undefined,
                imageFiles: normalizedDocument.imageFiles,
              },
              actorId,
            )
          : await pmService.updateDocProperties(
              draft.id,
              [{ name, value }],
              actorId,
            );
      setDraft((current) => ({
        ...current,
        ...saved,
        title: saved?.title ?? current.title,
        slug: saved?.slug ?? current.slug,
        content: saved?.content ?? current.content,
        updatedAt: saved?.updatedAt ?? current.updatedAt,
      }));
      setEditingProperty("");
      message.success("Document updated.");
      if (name === "title") {
        await loadTree({ keepSelection: true });
      }
    } catch (error) {
      message.error(error?.message || "Unable to update document.");
    } finally {
      setSavingProperty("");
    }
  };

  const deleteDoc = async (doc) => {
    if (!doc?.id) return;
    try {
      await pmService.deleteDoc(doc.id, actorId);
      message.success("Doc deleted.");
      setSelectedId("");
      setDraft(emptyDoc);
      setEditingProperty("");
      navigateToDocs("", { replace: true });
      await loadTree({ keepSelection: false });
    } catch (error) {
      message.error(error?.message || "Unable to delete doc.");
    }
  };

  const handlers = useMemo(
    () => ({
      addChild,
      addSibling,
      editTitle: editDocTitle,
      confirmDelete: (doc) => {
        const descendantCount = Math.max(0, flattenDocs(doc.children || []).length);
        Modal.confirm({
          title: "Delete document?",
          content:
            descendantCount
              ? `This document contains ${descendantCount} nested subdocument${descendantCount === 1 ? "" : "s"}. Deleting it may also remove its descendants.`
              : "This action cannot be undone.",
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => deleteDoc(doc),
        });
      },
    }),
    [selectedDoc, actorId, editDocTitle],
  );

  const treeData = useMemo(
    () => toTreeData(docs, selectedId, handlers),
    [docs, selectedId, handlers],
  );
  const isContentEditing = hasActiveDraft && editingProperty === "content";
  const isDocumentWorkspace = hasActiveDraft;
  const saveActiveDocument = draft.id && !isNew ? () => saveDocProperty("content") : saveDoc;
  const activeDocumentSaving = draft.id && !isNew ? savingProperty === "content" : saving;

  return (
    <div
      className={`docs-page${isTreeCollapsed ? " is-tree-collapsed" : ""}${
        isContentEditing ? " is-content-editing" : ""
      }${isDocumentWorkspace ? " is-document-workspace" : ""}`}
    >
      <style>{`
        .docs-page {
          position: relative;
          display: flex;
          flex-direction: row;
          width: 100%;
          height: 100%;
          min-height: 0;
          margin: 0;
          border: 0;
          border-radius: 0;
          overflow: hidden;
          background: #f7faf8;
          box-shadow: none;
        }
        .docs-page.is-tree-collapsed {
          display: flex;
        }
        .docs-page.is-tree-collapsed .docs-tree-panel {
          flex: 0 0 44px;
          width: 44px;
          min-width: 44px;
          max-width: 44px;
        }
        .docs-page.is-document-workspace .docs-view-panel,
        .docs-page.is-document-workspace .docs-editor {
          position: relative;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }
        .docs-page.is-document-workspace .docs-view-panel {
          flex: 1 1 0;
          width: auto;
        }
        .docs-page.is-document-workspace .docs-editor {
          width: 100%;
        }
        .docs-page.is-document-workspace .docs-read-view,
        .docs-page.is-document-workspace .docs-edit-surface {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          min-height: 0;
          margin: 0;
        }
        .docs-tree-panel {
          flex: 0 0 286px;
          width: 286px;
          padding: 0;
          border-right: 1px solid #dbe7df;
          background: #f8fbf9;
          min-width: 0;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: flex-basis .22s ease, width .22s ease, min-width .22s ease, max-width .22s ease;
        }
        .docs-rail-head,
        .docs-workspace-head {
          height: 54px;
          min-height: 54px;
          max-height: 54px;
          border-bottom: 1px solid #dbe7df;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          flex-shrink: 0;
        }
        .docs-rail-head {
          height: 76px;
          min-height: 76px;
          max-height: 76px;
          padding: 10px 16px;
          background: #ffffff;
        }
        .docs-page.is-tree-collapsed .docs-rail-head {
          justify-content: center;
          padding: 0;
        }
        .docs-rail-heading {
          min-width: 0;
        }
        .docs-rail-eyebrow {
          color: ${OLIVEEB_COLORS.oliveebGreen};
          font-size: 14px;
          font-weight: 900;
          letter-spacing: 0;
          line-height: 1;
        }
        .docs-rail-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          margin-top: 2px;
        }
        .docs-rail-title {
          color: ${OLIVEEB_COLORS.oliveebDarkGreen};
          font-size: 22px;
          font-weight: 900;
          line-height: 1.15;
        }
        .docs-rail-count {
          min-width: 28px;
          height: 28px;
          padding: 0 8px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #eef7f0;
          color: ${OLIVEEB_COLORS.oliveebGreen};
          font-size: 14px;
          font-weight: 800;
        }
        .docs-rail-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }
        .docs-rail-actions .ant-btn {
          width: 38px;
          height: 38px;
          padding: 0;
          border-radius: 6px;
          font-size: 18px;
        }
        .docs-rail-actions .ant-btn-primary {
          width: 42px;
          height: 42px;
          border-radius: 7px;
          box-shadow: 0 0 0 4px rgba(22, 119, 255, 0.18);
        }
        .docs-collapsed-rail {
          flex: 1;
          min-height: 0;
          padding: 8px 4px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          background: #f8fbf9;
        }
        .docs-collapsed-icon-button {
          position: relative;
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 8px;
          display: grid;
          place-items: center;
          background: transparent;
          color: ${OLIVEEB_COLORS.textMuted};
          cursor: pointer;
          transition: background .15s, color .15s;
        }
        .docs-collapsed-icon-button:hover,
        .docs-collapsed-icon-button.is-active {
          background: #eef7f0;
          color: ${OLIVEEB_COLORS.oliveebGreen};
        }
        .docs-collapsed-icon-button .docs-collapsed-tip {
          display: none;
          position: absolute;
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
          z-index: 90;
          white-space: nowrap;
          border: 1px solid #dbe7df;
          border-radius: 5px;
          padding: 4px 9px;
          background: #ffffff;
          color: ${OLIVEEB_COLORS.text};
          font-size: 11px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
          pointer-events: none;
        }
        .docs-collapsed-icon-button:hover .docs-collapsed-tip {
          display: block;
        }
        .docs-collapsed-count {
          position: absolute;
          top: 3px;
          right: 3px;
          min-width: 13px;
          height: 13px;
          padding: 0 3px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #eef7f0;
          color: ${OLIVEEB_COLORS.oliveebGreen};
          font-size: 8px;
          font-weight: 900;
          line-height: 1;
        }
        .docs-tree-scroll {
          flex: 1;
          min-height: 0;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          padding: 12px 8px 12px 18px;
        }
        .docs-tree-panel .ant-tree-indent-unit {
          width: 28px;
        }
        .docs-tree-panel .ant-tree-switcher {
          width: 22px;
        }
        .docs-tree-panel .ant-tree-show-line .ant-tree-indent-unit::before {
          border-inline-end-color: #d6e5dc;
        }
        .docs-tree-panel .ant-tree {
          background: transparent;
          font-size: 15px;
        }
        .docs-tree-panel .ant-tree .ant-tree-treenode {
          padding: 0 0 6px;
        }
        .docs-tree-panel .ant-tree .ant-tree-node-content-wrapper {
          min-height: 34px;
          padding: 0;
          border-radius: 6px;
        }
        .docs-tree-panel .ant-tree .ant-tree-node-content-wrapper:hover,
        .docs-tree-panel .ant-tree .ant-tree-node-selected {
          background: transparent;
        }
        .docs-view-panel {
          min-width: 0;
          min-height: 0;
          height: 100%;
          padding: 0;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          background: #ffffff;
        }
        .docs-workspace-head {
          padding: 8px 14px;
          background: #ffffff;
          flex-shrink: 0;
          min-height: 56px;
        }
        .docs-workspace-head-main {
          min-width: 0;
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .docs-tree-toggle {
          flex-shrink: 0;
        }
        .docs-editor {
          padding: 0;
          overflow: hidden;
          flex: 1 1 auto;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: #ffffff;
        }
        .docs-edit-surface {
          position: relative;
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: #ffffff;
        }
        .docs-content-surface {
          flex: 1 1 auto;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          cursor: text;
        }
        .docs-content-surface .docs-word-editor {
          flex: 1 1 auto;
          min-height: 0;
        }
        .docs-content-surface:not(.is-editing) .ql-toolbar {
          display: none;
        }
        .docs-content-surface:not(.is-editing) .ql-container {
          border-top: 1px solid #ccc;
        }
        .docs-new-fields {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: minmax(160px, 1fr) minmax(140px, 220px);
          gap: 0;
          border-bottom: 1px solid #e3ece6;
          background: #ffffff;
        }
        .docs-new-fields .ant-input {
          height: 38px;
          border: 0;
          border-right: 1px solid #edf2ef;
          border-radius: 0;
          box-shadow: none;
          font-size: 12px;
        }
        .docs-new-fields .ant-input:last-child {
          border-right: 0;
        }
        .docs-editor .ant-space {
          flex: 1;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .docs-editor .ant-space-item:last-child {
          flex: 1;
          min-height: 0;
          display: flex;
        }
        .docs-tree-title {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          min-height: 34px;
          padding: 5px 4px;
          border-radius: 6px;
        }
        .docs-tree-title.is-selected {
          background: #eef7f0;
          color: ${OLIVEEB_COLORS.oliveebGreen};
          font-weight: 800;
        }
        .docs-tree-name {
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .docs-tree-label {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .docs-create-menu .ant-dropdown-menu {
          min-width: 268px;
          padding: 10px;
          border-radius: 12px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16);
        }
        .docs-create-menu .ant-dropdown-menu-item {
          min-height: 44px;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 16px;
        }
        .docs-title-input {
          border: 0;
          box-shadow: none;
          padding: 0;
          font-size: 28px;
          font-weight: 900;
          color: ${OLIVEEB_COLORS.oliveebDarkGreen};
        }
        .docs-title-input:focus {
          box-shadow: none;
        }
        .docs-meta {
          color: ${OLIVEEB_COLORS.textMuted};
          font-size: 11px;
          line-height: 1.2;
        }
        .docs-doc-summary {
          min-width: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .docs-doc-path {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px;
          min-width: 0;
          color: ${OLIVEEB_COLORS.textMuted};
          font-size: 11px;
          line-height: 1.15;
        }
        .docs-doc-path-link {
          border: 0;
          background: transparent;
          padding: 0;
          color: ${OLIVEEB_COLORS.oliveebGreen};
          font: inherit;
          cursor: pointer;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .docs-doc-path-link:hover {
          text-decoration: underline;
        }
        .docs-doc-path-current {
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .docs-doc-path-segment {
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .docs-doc-path-separator {
          color: ${OLIVEEB_COLORS.textMuted};
        }
        .docs-doc-title {
          margin: 0;
          color: ${OLIVEEB_COLORS.oliveebDarkGreen};
          font-size: 15px;
          font-weight: 900;
          line-height: 1.05;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: text;
        }
        .docs-inline-edit {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: min(420px, 100%);
        }
        .docs-inline-edit .ant-input {
          height: 26px;
          font-size: 13px;
        }
        .docs-property-chip {
          cursor: text;
          border-radius: 4px;
          padding: 1px 2px;
        }
        .docs-property-chip:hover,
        .docs-doc-title:hover {
          background: rgba(0, 82, 64, 0.06);
        }
        .docs-doc-meta-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
        }
        .docs-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 6px;
        }
        .docs-read-view {
          position: relative;
          flex: 1;
          height: 100%;
          min-height: 0;
          display: flex;
          flex-direction: column;
          padding: 0;
          overflow: hidden;
          background: #ffffff;
          border: 0;
          border-radius: 0;
        }
        .docs-content-editor-actions {
          position: absolute;
          top: 6px;
          right: 10px;
          z-index: 60;
          display: flex;
          justify-content: flex-end;
          margin: 0;
          padding: 0;
          background: transparent;
          border-bottom: 0;
          backdrop-filter: none;
        }
        .docs-floating-actions {
          position: absolute;
          top: 8px;
          right: 10px;
          z-index: 75;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
          padding: 4px;
          border: 1px solid rgba(214, 226, 220, 0.9);
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(8px);
        }
        .docs-floating-actions .ant-btn {
          box-shadow: none;
        }
        .docs-toolbar-actions {
          position: absolute;
          top: 7px;
          right: 8px;
          z-index: 50;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .docs-toolbar-actions .ant-btn {
          height: 30px;
          border-radius: 4px;
          box-shadow: none;
          font-size: 12px;
          font-weight: 700;
        }
        .docs-content-editor-actions.docs-floating-actions {
          top: 8px;
          right: 10px;
          padding: 4px;
          border-bottom: 1px solid rgba(214, 226, 220, 0.9);
          background: rgba(255, 255, 255, 0.92);
        }
        .docs-read-content {
          flex: 1;
          min-height: 0;
          height: 100%;
          overflow: auto;
          margin: 0;
          color: ${OLIVEEB_COLORS.text};
          font-size: 15px;
          line-height: 1.7;
          cursor: text;
          border-radius: 0;
          padding: 0 24px;
        }
        .docs-read-content:hover {
          background: #ffffff;
        }
        .docs-read-content:empty::before {
          content: "No content yet. Double-click to start editing.";
          color: ${OLIVEEB_COLORS.textMuted};
        }
        .docs-read-content p {
          margin: 0 0 12px;
        }
        .docs-read-content img,
        .docs-editor .ql-editor img {
          max-width: 100%;
          height: auto;
          display: inline-block;
          vertical-align: middle;
          margin: 6px 4px;
          position: relative;
          z-index: 1;
        }
        .docs-read-content img:not([width]):not([style*="width"]),
        .docs-editor .ql-editor img:not([width]):not([style*="width"]) {
          width: min(480px, 100%);
        }
        .docs-editor .ql-editor p,
        .docs-read-content p {
          overflow: visible;
        }
        .docs-read-content ul,
        .docs-read-content ol {
          padding-left: 24px;
        }
        .docs-read-content blockquote {
          border-left: 4px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
          margin: 16px 0;
          padding-left: 14px;
          color: ${OLIVEEB_COLORS.textMuted};
        }
        .docs-word-editor {
          flex: 1;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          margin-bottom: 0 !important;
        }
        .docs-word-editor > div {
          flex: 1;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .docs-word-editor .auto-grow-editor {
          flex: 1;
          min-height: 0;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border-radius: 0;
          background: #ffffff;
        }
        .docs-word-editor .ql-toolbar.ql-snow {
          position: sticky;
          top: 0;
          z-index: 35;
          margin: 0;
          min-height: 48px;
          padding: 7px 92px 7px 8px;
          border: 0;
          border-bottom: 1px solid #d6e2dc;
          border-radius: 0;
          background: #ffffff;
          box-shadow: none;
          backdrop-filter: none;
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
        }
        .docs-word-editor .ql-toolbar.ql-snow::-webkit-scrollbar {
          display: none;
        }
        .docs-word-editor .ql-toolbar.ql-snow .ql-formats {
          display: inline-flex;
          align-items: center;
          margin-right: 8px;
        }
        .docs-word-editor .ql-toolbar.ql-snow button {
          width: 24px;
          height: 26px;
          padding: 3px;
        }
        .docs-word-editor .ql-toolbar.ql-snow .ql-picker {
          height: 28px;
          font-size: 12px;
        }
        .docs-word-editor .ql-toolbar.ql-snow .ql-picker-label {
          display: flex;
          align-items: center;
          border: 0;
          padding-left: 4px;
        }
        .docs-word-editor .ql-toolbar.ql-snow .ql-picker.ql-header {
          width: 100px;
        }
        .docs-word-editor .ql-toolbar.ql-snow .ql-picker.ql-font {
          width: 122px;
        }
        .docs-word-editor .rte-frame {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .docs-word-editor .ql-container.ql-snow {
          flex: 1 1 auto;
          height: auto;
          min-height: 0;
          border: 0;
          border-radius: 0;
          overflow: hidden;
          background: #ffffff;
          padding: 0;
        }
        .docs-word-editor .ql-editor {
          width: 100%;
          flex: 1;
          min-height: 100%;
          height: 100%;
          margin: 0;
          box-sizing: border-box;
          overflow-x: hidden;
          overflow-y: auto;
          padding: 22px 28px 32px;
          color: ${OLIVEEB_COLORS.text};
          font-size: 13px;
          line-height: 1.8;
          background: #ffffff;
          box-shadow: none;
        }
        .docs-word-editor .ql-editor.ql-blank::before {
          left: 28px;
          right: 28px;
          color: ${OLIVEEB_COLORS.textMuted};
          font-style: normal;
        }
        .docs-word-editor .ql-editor p,
        .docs-word-editor .ql-editor ul,
        .docs-word-editor .ql-editor ol,
        .docs-word-editor .ql-editor blockquote {
          max-width: 100%;
        }
        .docs-promo {
          width: 100%;
          height: 100%;
          min-height: 0;
          flex: 1 1 auto;
          display: flex;
          padding: 0;
          background: #ffffff;
          border: 0;
          border-radius: 0;
          overflow: auto;
        }
        .docs-promo-inner {
          width: 100%;
          height: 100%;
          min-height: 0;
          border: 0;
          border-radius: 0;
          padding: 28px 30px;
          background: transparent;
          box-shadow: none;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }
        .docs-promo-icon {
          width: 54px;
          height: 54px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${OLIVEEB_COLORS.white};
          background: ${OLIVEEB_COLORS.oliveebGreen};
          font-size: 24px;
          margin-bottom: 18px;
        }
        .docs-promo-points {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
          margin: 22px 0 0;
        }
        .docs-promo-point {
          border: 1px solid #e5eee8;
          border-radius: 8px;
          padding: 14px;
          background: #fbfdfc;
        }
        @media (max-width: 900px) {
          .docs-page {
            grid-template-columns: 1fr;
          }
          .docs-tree-panel {
            border-right: 0;
            border-bottom: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
          }
          .docs-tree-scroll {
            height: 280px;
          }
          .docs-promo {
            padding: 0;
          }
          .docs-promo-inner {
            padding: 24px;
          }
          .docs-promo-points {
            grid-template-columns: 1fr;
          }
          .docs-word-editor .ql-container.ql-snow {
            padding: 0;
          }
          .docs-word-editor .ql-editor {
            min-height: 100%;
            padding: 0 18px;
            background: #ffffff;
          }
          .docs-word-editor .ql-editor.ql-blank::before {
            left: 18px;
            right: 18px;
          }
        }
      `}</style>

      <section className="docs-tree-panel">
        <div className="docs-rail-head">
          {isTreeCollapsed ? (
            <Button
              className="docs-tree-toggle"
              size="small"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setIsTreeCollapsed(false)}
              title="Show documents"
              aria-label="Show documents"
            />
          ) : (
            <>
              <div className="docs-rail-heading">
                <div className="docs-rail-eyebrow">DOCUMENTS</div>
                <div className="docs-rail-title-row">
                  <span className="docs-rail-title">Docs</span>
                  <span className="docs-rail-count">{flatDocs.length}</span>
                </div>
              </div>
              <div className="docs-rail-actions">
                <Button
                  className="docs-tree-toggle"
                  size="small"
                  icon={<MenuFoldOutlined />}
                  onClick={() => setIsTreeCollapsed(true)}
                  title="Collapse documents"
                  aria-label="Collapse documents"
                />
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => loadTree()}
                  loading={loadingTree}
                  title="Refresh"
                />
                <Dropdown
                  trigger={["click"]}
                  overlayClassName="docs-create-menu"
                  menu={{
                    items: [
                      { key: "root", label: "Add", icon: <PlusOutlined /> },
                      { key: "child", label: "Add inside selected", icon: <PlusOutlined />, disabled: !selectedDoc },
                      { key: "sibling", label: "Add beside selected", icon: <PlusOutlined />, disabled: !selectedDoc },
                    ],
                    onClick: ({ key }) => {
                      if (key === "root") addRoot();
                      if (key === "child") addChild();
                      if (key === "sibling") addSibling();
                    },
                  }}
                >
                  <Button size="small" type="primary" icon={<PlusOutlined />} title="New document" />
                </Dropdown>
              </div>
            </>
          )}
        </div>
        {isTreeCollapsed ? (
          <div className="docs-collapsed-rail" aria-label="Collapsed documents rail">
            <button
              type="button"
              className="docs-collapsed-icon-button is-active"
              onClick={() => setIsTreeCollapsed(false)}
              title="Docs"
              aria-label="Show documents"
            >
              <FileTextOutlined />
              <span className="docs-collapsed-count">{flatDocs.length}</span>
              <span className="docs-collapsed-tip">Docs</span>
            </button>
            <button
              type="button"
              className="docs-collapsed-icon-button"
              onClick={addRoot}
              title="New document"
              aria-label="New document"
            >
              <PlusOutlined />
              <span className="docs-collapsed-tip">New document</span>
            </button>
          </div>
        ) : (
          <>
            <div className="docs-tree-scroll">
              {loadingTree ? (
                <Spin />
              ) : hasDocs ? (
                <Tree
                  blockNode
                  showLine
                  treeData={treeData}
                  selectedKeys={selectedId ? [selectedId] : []}
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => setExpandedKeys(keys)}
                  onSelect={(keys, info) => selectDoc(keys[0] || info?.node?.key || "")}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No docs yet"
                >
                  <Button type="primary" icon={<PlusOutlined />} onClick={addRoot}>
                    Add document
                  </Button>
                </Empty>
              )}
            </div>
          </>
        )}
      </section>

      <section className="docs-view-panel">
        <div className="docs-workspace-head">
          <div className="docs-workspace-head-main">
            <div className="docs-doc-summary">
              {hasActiveDraft ? (
                <>
                  {docPath.length ? (
                    <div className="docs-doc-path" aria-label="Document path">
                      {docPath.map((doc, index) => {
                        const isLast = index === docPath.length - 1;
                        return (
                          <span key={doc.id || `draft-${index}`} className="docs-doc-path-segment">
                            {index > 0 ? <span className="docs-doc-path-separator">/</span> : null}
                            {doc.id && !isLast ? (
                              <button
                                type="button"
                                className="docs-doc-path-link"
                                onClick={() => selectDoc(doc.id)}
                                title={doc.title || "Untitled doc"}
                              >
                                {doc.title || "Untitled doc"}
                              </button>
                            ) : (
                              <span
                                className="docs-doc-path-current"
                                title={doc.title || "Untitled doc"}
                              >
                                {doc.title || "Untitled doc"}
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  {draft.id && editingProperty === "title" ? (
                    <div className="docs-inline-edit">
                      <Input
                        value={draft.title}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, title: event.target.value }))
                        }
                        onPressEnter={() => saveDocProperty("title")}
                        autoFocus
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={savingProperty === "title"}
                        onClick={() => saveDocProperty("title")}
                      >
                        Save title
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingProperty("");
                          loadDoc(draft.id);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <h2
                      className="docs-doc-title"
                      title="Double-click to rename"
                      onDoubleClick={() => draft.id && setEditingProperty("title")}
                    >
                      {draft.title || "Untitled doc"}
                    </h2>
                  )}
                </>
              ) : (
                <>
                  <h2 className="docs-doc-title">Docs</h2>
                  <Text className="docs-meta">Select a document or create a new one</Text>
                </>
              )}
            </div>
          </div>
          {hasActiveDraft ? (
            <div className="docs-actions">
              {draft.id ? (
                <Button size="small" icon={<LinkOutlined />} onClick={copyDocLink}>
                  Copy link
                </Button>
              ) : null}
              {draft.id ? (
                <Button
                  size="small"
                  icon={<PaperClipOutlined />}
                  onClick={() => setAttachmentsOpen(true)}
                >
                  Files {docAttachments.length ? `(${docAttachments.length})` : ""}
                </Button>
              ) : null}
              {draft.id ? (
                <Popconfirm
                  title="Delete document?"
                  description="Nested subdocuments may also be affected."
                  okButtonProps={{ danger: true }}
                  okText="Delete"
                  onConfirm={() => deleteDoc(draft)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                </Popconfirm>
              ) : null}
              {isNew ? (
                <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveDoc} loading={saving}>
                  Save
                </Button>
              ) : null}
            </div>
          ) : (
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={addRoot}>
              Add document
            </Button>
          )}
        </div>
        <div className="docs-editor">
          {loadingDoc ? (
            <Spin />
          ) : hasActiveDraft ? (
            <div className="docs-edit-surface">
              <div className="docs-toolbar-actions">
                  {draft.id && !isNew ? (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      disabled={isContentEditing}
                      onClick={() => setEditingProperty("content")}
                    >
                      Edit
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={saveActiveDocument}
                    loading={activeDocumentSaving}
                    disabled={Boolean(draft.id && !isNew && !isContentEditing)}
                  >
                    Save
                  </Button>
              </div>
              {isNew ? (
                <div className="docs-new-fields">
                  <Input
                    className="docs-title-input"
                    placeholder="Untitled doc"
                    value={draft.title}
                    onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                  />
                  <Input
                    placeholder="Slug"
                    value={draft.slug || ""}
                    onChange={(event) => setDraft((current) => ({ ...current, slug: event.target.value }))}
                  />
                </div>
              ) : null}
              <div
                className={`docs-content-surface${isContentEditing || isNew ? " is-editing" : ""}`}
                title={!isContentEditing && !isNew ? "Double-click to edit" : undefined}
                onDoubleClick={() => {
                  if (draft.id && !isContentEditing) {
                    setEditingProperty("content");
                  }
                }}
              >
                <RichTextEditor
                  key={isContentEditing || isNew ? "editing" : "readonly"}
                  className="docs-word-editor"
                  value={draft.content || ""}
                  onChange={updateDraftContent}
                  placeholder="Write the document content..."
                  height="100%"
                  fullHeight
                  noMargin
                  readOnly={Boolean(draft.id && !isNew && !isContentEditing)}
                  onImageUpload={handleDocImageUpload}
                  onImagePaste={handleDocImageUpload}
                />
              </div>
            </div>
          ) : (
            <div className="docs-promo">
              <div className="docs-promo-inner">
                <div className="docs-promo-icon">
                  <FileTextOutlined />
                </div>
                <Title level={2} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Build a living operations library
                </Title>
                <Paragraph style={{ margin: "12px 0 0", color: OLIVEEB_COLORS.textMuted, fontSize: 16 }}>
                  Use Docs to organize team knowledge, procedures, decisions, and project references in one structured place.
                </Paragraph>
                <div className="docs-promo-points">
                  <div className="docs-promo-point">
                    <Text strong>Tree structure</Text>
                    <Paragraph style={{ margin: "6px 0 0", color: OLIVEEB_COLORS.textMuted }}>
                      Arrange pages in a clear hierarchy that matches how your team works.
                    </Paragraph>
                  </div>
                  <div className="docs-promo-point">
                    <Text strong>Operational clarity</Text>
                    <Paragraph style={{ margin: "6px 0 0", color: OLIVEEB_COLORS.textMuted }}>
                      Keep procedures and reference notes close to the work.
                    </Paragraph>
                  </div>
                  <div className="docs-promo-point">
                    <Text strong>Shared context</Text>
                    <Paragraph style={{ margin: "6px 0 0", color: OLIVEEB_COLORS.textMuted }}>
                      Give teams a reliable place to find current guidance.
                    </Paragraph>
                  </div>
                </div>
                <Space style={{ marginTop: 24 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={addRoot}>
                    Create document
                  </Button>
                  {hasDocs ? (
                    <Text style={{ color: OLIVEEB_COLORS.textMuted }}>
                      Or select a document from the tree.
                    </Text>
                  ) : null}
                </Space>
              </div>
            </div>
          )}
        </div>
      </section>

      <Modal
        title="Document files"
        open={attachmentsOpen}
        footer={null}
        onCancel={() => setAttachmentsOpen(false)}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Upload
            multiple
            showUploadList={false}
            disabled={uploadingDocAttachment || !draft.id}
            customRequest={({ file, onError, onSuccess }) => {
              handleDocAttachmentUpload(file)
                .then(() => onSuccess?.("ok"))
                .catch((error) => onError?.(error));
            }}
          >
            <Button type="primary" icon={<UploadOutlined />} loading={uploadingDocAttachment}>
              Attach file
            </Button>
          </Upload>
        </div>
        {loadingDocAttachments ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : docAttachments.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No files attached" />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {docAttachments.map((attachment) => {
              const name = attachment.label || attachment.fileName || attachment.fileId || "Attachment";
              return (
                <div
                  key={attachment.id || attachment.fileId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <PaperClipOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
                      {attachment.fileUrl ? (
                        <a
                          href={attachment.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {name}
                        </a>
                      ) : (
                        <Text style={{ overflowWrap: "anywhere" }}>{name}</Text>
                      )}
                    </div>
                    {attachment.createdAt ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Attached {dayjs(attachment.createdAt).format("MMM D, YYYY h:mm A")}
                      </Text>
                    ) : null}
                  </div>
                  <Popconfirm
                    title="Delete attachment?"
                    okText="Delete"
                    okButtonProps={{ danger: true, loading: deletingDocAttachmentId === attachment.id }}
                    onConfirm={() => handleDeleteDocAttachment(attachment.id)}
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Docs;
